import React, { useRef, useState, useCallback, useEffect } from "react";

// simple debounce hook
function useDebounce(func, delay) {
  const timeout = useRef(null);
  const debounced = useCallback(
    (...args) => {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => func(...args), delay);
    },
    [func, delay]
  );
  useEffect(() => {
    return () => clearTimeout(timeout.current);
  }, []);
  return debounced;
}

// Dual-thumb slider: immediate onChange during drag, debounced onAfterChange on release
const DualThumbSlider = ({
  min,
  max,
  value,
  onChange,
  onAfterChange,
  disabled = false,
  debounceTime = 100,
}) => {
  // derive prop values
  const [propMin, propMax] = Array.isArray(value) ? value : [min, max];
  // local state during drag
  const [localMin, setLocalMin] = useState(propMin);
  const [localMax, setLocalMax] = useState(propMax);
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);

  const getPercentage = (val) => ((val - min) / (max - min)) * 100;
  const getValue = (percentage) =>
    Math.round(min + (percentage / 100) * (max - min));

  const handleMouseDown = useCallback(
    (thumb) => (e) => {
      if (disabled) return;
      e.preventDefault();
      setIsDragging(thumb);
    },
    [disabled]
  );

  // debounced callback to propagate final value
  const debouncedAfterChange = useDebounce(
    onAfterChange || onChange || (() => {}),
    debounceTime
  );
  // sync local state when prop values change
  useEffect(() => {
    setLocalMin(propMin);
    setLocalMax(propMax);
  }, [propMin, propMax]);
  // update local values during drag
  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const percentage = Math.max(
        0,
        Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
      );
      const newValue = getValue(percentage);
      if (isDragging === "min") {
        const newMin = Math.min(newValue, localMax - 1);
        setLocalMin(newMin);
      } else {
        const newMax = Math.max(newValue, localMin + 1);
        setLocalMax(newMax);
      }
    },
    [isDragging, localMin, localMax]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
    debouncedAfterChange([localMin, localMax]);
  }, [debouncedAfterChange, localMin, localMax]);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const minPercent = getPercentage(localMin);
  const maxPercent = getPercentage(localMax);

  return (
    <div
      className={`relative w-full h-12 ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
    >
      {/* Slider track */}
      <div
        ref={sliderRef}
        className={`absolute left-0 top-2 w-full h-1 bg-gray-300 rounded-full ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        {/* Active track between thumbs */}
        <div
          className={`absolute h-full rounded-full ${
            disabled ? "bg-gray-400" : "bg-blue-500"
          }`}
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />

        {/* Min thumb */}
        <div
          className={`absolute w-4 h-4 rounded-full border-2 border-white shadow-lg transform -translate-y-1/2 -translate-x-1/2 ${
            disabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 cursor-grab active:cursor-grabbing"
          }`}
          style={{ left: `${minPercent}%`, top: "50%" }}
          onMouseDown={handleMouseDown("min")}
        />

        {/* Max thumb */}
        <div
          className={`absolute w-4 h-4 rounded-full border-2 border-white shadow-lg transform -translate-y-1/2 -translate-x-1/2 ${
            disabled
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-500 cursor-grab active:cursor-grabbing"
          }`}
          style={{ left: `${maxPercent}%`, top: "50%" }}
          onMouseDown={handleMouseDown("max")}
        />
      </div>

      {/* Min/Max labels aligned with thumbs */}
      <div
        className="absolute top-8 text-[#2a2a2a] text-sm font-normal transform -translate-x-1/2"
        style={{ left: `${minPercent}%` }}
      >
        {localMin}
      </div>
      <div
        className="absolute top-8 text-[#2a2a2a] text-sm font-normal transform -translate-x-1/2"
        style={{ left: `${maxPercent}%` }}
      >
        {localMax}
      </div>
    </div>
  );
};

export default DualThumbSlider;
